import { CombinedGraphQLErrors } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { ActivityIndicator, Text, TouchableRipple, useTheme } from 'react-native-paper';

import { ContactFieldRow } from '@/components/molecules/contacts/ContactFieldRow';
import { ContactNotesSheet } from '@/components/molecules/list';
import {
  GetContactDocument,
  GetContactProfileAttributesOrderDocument,
  GetContactStatusesDocument,
} from '@/generated/usermobile-bff/graphql';
import { useTranslation } from '@/i18n';
import { rippleOnLink } from '@/theme';

import { resolveContactProfileRows } from './contactProfileFieldResolvers';

interface ContactProfileProps {
  readonly id: number;
  readonly bottomPadding?: number;
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#f2f4f5',
    flex: 1,
  },
  body: {
    gap: 16,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  loading: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  section: {
    gap: 4,
  },
  sectionTitle: {
    color: '#17344a',
    fontFamily: 'Lato-Bold',
    fontSize: 18,
    fontWeight: '700',
    lineHeight: 32,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 16,
  },
  cardText: {
    color: '#17344a',
    fontSize: 16,
    letterSpacing: 0.5,
    lineHeight: 24,
  },
  seeMoreLink: {
    color: '#1864ab',
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: 0.1,
    lineHeight: 20,
    paddingTop: 10,
  },
  seeMoreTouchable: {
    alignSelf: 'flex-start',
    borderRadius: 4,
    overflow: 'hidden',
  },
  detailsCard: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    paddingBottom: 16,
  },
  fieldDivider: {
    backgroundColor: '#d5dfe6',
    height: StyleSheet.hairlineWidth,
    marginHorizontal: 16,
  },
  errorText: {
    fontSize: 14,
    textAlign: 'center',
  },
});

export const ContactProfile = ({ id, bottomPadding = 50 }: ContactProfileProps) => {
  const { t, locale } = useTranslation();
  const theme = useTheme();
  const [notesSheetVisible, setNotesSheetVisible] = useState(false);

  // This screen owns deliberate all-or-nothing error UX (including FORBIDDEN),
  // so it opts out of the global 'all' error policy.
  const contactQuery = useQuery(GetContactDocument, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'none',
  });
  const preferenceQuery = useQuery(GetContactProfileAttributesOrderDocument, {
    fetchPolicy: 'cache-and-network',
  });
  const contactStatusesQuery = useQuery(GetContactStatusesDocument, {
    fetchPolicy: 'cache-first',
  });
  const contactStatusNames = useMemo(
    () =>
      new Map(
        (contactStatusesQuery.data?.contactStatuses.data ?? []).flatMap((status) =>
          status.name == null ? [] : [[status.id, status.name] as const],
        ),
      ),
    [contactStatusesQuery.data],
  );

  const handleOpenNotes = useCallback(() => {
    setNotesSheetVisible(true);
  }, []);

  const handleDismissNotes = useCallback(() => {
    setNotesSheetVisible(false);
  }, []);

  const isLoadingWithoutData =
    (contactQuery.loading && !contactQuery.data) ||
    (preferenceQuery.loading && !preferenceQuery.data);

  if (isLoadingWithoutData) {
    return (
      <View style={[styles.container, styles.loading]}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  const isForbidden = [contactQuery.error, preferenceQuery.error].some(
    (error) =>
      CombinedGraphQLErrors.is(error) &&
      error.errors.some((item) => item.extensions?.['code'] === 'FORBIDDEN'),
  );

  if (isForbidden) {
    return (
      <View style={[styles.container, styles.loading]}>
        <Text style={[styles.errorText, { color: theme.colors.error }]}>
          {t('contacts.profile.accessDenied', {
            defaultValue: 'You do not have permission to view this contact.',
          })}
        </Text>
      </View>
    );
  }

  const hasQueryError = Boolean(contactQuery.error || preferenceQuery.error);

  if (hasQueryError) {
    return (
      <View style={[styles.container, styles.loading]}>
        <Text style={[styles.errorText, { color: theme.colors.error }]}>
          {t('contacts.profile.loadError')}
        </Text>
      </View>
    );
  }

  const contact = contactQuery.data?.contact;
  const contactProfileAttributesOrder = preferenceQuery.data?.contactProfileAttributesOrder;

  const hasMissingRequiredData = !contact || !contactProfileAttributesOrder;

  if (hasMissingRequiredData) {
    return (
      <View style={[styles.container, styles.loading]}>
        <Text style={[styles.errorText, { color: theme.colors.error }]}>
          {t('contacts.profile.loadError')}
        </Text>
      </View>
    );
  }

  const contactName = contact.fullName ?? `#${contact.id}`;
  const notes = contact.notes ?? '';
  const fields = resolveContactProfileRows({
    contact,
    contactStatusNames,
    preferences: contactProfileAttributesOrder,
    t,
    locale,
  });

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={[styles.body, { paddingBottom: bottomPadding }]}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('contacts.picker.memoSection')}</Text>
          <View style={styles.card}>
            {notes ? (
              <>
                <Text style={styles.cardText} numberOfLines={6} ellipsizeMode="tail">
                  {notes}
                </Text>
                <TouchableRipple
                  onPress={handleOpenNotes}
                  rippleColor={rippleOnLink}
                  style={styles.seeMoreTouchable}
                  testID="contact-profile-see-more">
                  <Text style={styles.seeMoreLink}>{t('contacts.picker.seeMore')}</Text>
                </TouchableRipple>
              </>
            ) : (
              <Text style={styles.cardText}>{t('contacts.profile.noMemo')}</Text>
            )}
          </View>
        </View>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('contacts.picker.detailsSection')}</Text>
          {fields.length === 0 ? (
            <View style={styles.card}>
              <Text style={styles.cardText}>{t('contacts.profile.noDetails')}</Text>
            </View>
          ) : (
            <View style={styles.detailsCard}>
              {fields.map((field, index) => (
                <Fragment key={field.key}>
                  {index > 0 && <View style={styles.fieldDivider} />}
                  <ContactFieldRow label={field.label} value={field.value} />
                </Fragment>
              ))}
            </View>
          )}
        </View>
      </ScrollView>
      <ContactNotesSheet
        visible={notesSheetVisible}
        contactName={contactName}
        notes={notes}
        onDismiss={handleDismissNotes}
      />
    </View>
  );
};
